import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { decodeJwt } from "jose";
import { useHistory } from "react-router-dom";
import { Button, Container, Form, Spinner } from "react-bootstrap";
import LoginModal from "../LoginModal";
import PasswordControl from "../PasswordControl";
import { TriyoForm } from "../TriyoForm";
import { UserLogin, AzureUserLogin } from "../../services/api/authentication-api";
import { setAuthCookie } from "../../services/api/api-util.js";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../../authConfigAzureAd.js";
import "./Login.scss";

const msalInstance = new PublicClientApplication(msalConfig);

const Login = ({ onLogin }) => {
  const history = useHistory();
  const localStorage = window.localStorage;
  const [validated, setValidated] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem("loginUsername"));
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(
    !!localStorage.getItem("loginUsername")
  );
  const [errorMessage, setErrorMessage] = useState(false);
  const [errorMessageText, setErrorMessageText] = useState("");
  const [loading, setLoading] = useState(false);
  const isSelfServe = window["APP_SETTINGS"].REACT_APP_SELFSERVE_USER === "TRUE";
  const signupURL = window["APP_SETTINGS"].REACT_APP_SELFSERVE_SIGNUP_URL;
  const authType = window["APP_SETTINGS"].AUTH_TYPE;

  useEffect(() => {
    const authAzureAdInit = async () => {
      await msalInstance.initialize();
    };
    authAzureAdInit();
  }, []);

  const onEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const onSubmit = async () => {
    setValidated(true);

    if (!(email && password)) {
      return null;
    }

    setLoading(true);

    let currDateOffset = new Date().getTimezoneOffset();
    let loginObj = {
      username: email,
      password: password,
      timezone: currDateOffset,
    };

    await UserLogin(loginObj).then(
      (result) => {
        if (result.Token != null) {
          let resultToken = result.Token ?? "";
          let graphToken = result.GraphToken ?? "";

          if (rememberMe) {
            localStorage.setItem("loginUsername", email);
          }
          console.log("Login.js setAuthCookie -->");
          setAuthCookie(resultToken);
          onLogin({ resultToken, graphToken });
          history.push("/");
        } else {
          throw new Error("Token was not received");
        }
      },
      async (error) => {
        if (error.status === 403) {
          setErrorMessageText(
            "This User Account is no longer Active. Please contact your Administrator."
          );
        } else {
          setErrorMessageText(
            "Your email address/user name or password is incorrect."
          );
        }
        console.log(error);
        setErrorMessage(true);
      }
    );
    setLoading(false);
  };

  const forgotPassword = () => {
    history.push("/forgot-password");
  };

  const changeRememberMe = () => {
    if (rememberMe) {
      localStorage.removeItem("loginUsername");
    } else {
      localStorage.setItem("loginUsername", "");
    }
    setRememberMe(!rememberMe);
  };

  const loginAzureAd = async() => {
    console.log("Login.js loginAzureAd -->");
    await msalInstance
      .loginPopup({
        scopes: ["user.read"],
      })
      .then(async(response) => {
        console.log(response);
        console.log("Login.js ID Token:", response.idToken);
        console.log("Login.js Access Token:", response.accessToken);

        let accessToken = response.accessToken || "";
        let userJWT = decodeJwt(accessToken);
        let username = userJWT.upn || userJWT.unique_name || userJWT.preferred_username || "";
        let tenantId = userJWT.tid || "";
        let graphConnectorId = userJWT.appid || "";

        let currDateOffset = new Date().getTimezoneOffset();
        let data = {
          username: username,
          tenantId: tenantId,
          graphConnectorId: graphConnectorId,
          graphToken: accessToken,
          timezone: currDateOffset,
        };

        await AzureUserLogin(data).then(
          (result) => {
            if (result.Token != null) {
              let resultToken = result.Token ?? "";
              let graphToken = result.GraphToken ?? "";

              console.log("Login.js [AzureUserLogin] result -->");
              console.log(result);

              console.log("Login.js [AzureUserLogin] setAuthCookie -->");
              setAuthCookie(resultToken);
              onLogin({resultToken, graphToken});
              history.push("/");
            } else {
              throw new Error("[AzureUserLogin]: Token was not received");
            }
          },
          (error) => {
            console.log("Login.js AzureUserLogin [error]: ");
            console.log(error);
          }
        );
      })
      .catch((error) => {
        console.log("Login.js loginAzureAd error -->");
        console.log(error);
      });
  };

  return (
    <LoginModal>
      <Container className="login-form">
        { errorMessage && (
          <div className="error-message">{errorMessageText}</div>
        )}
        <h2>Login to your account</h2>
        { authType === "AzureAD" && (
          <button
            className="btn-primary"
            onClick={
              (e) => {
                e.preventDefault();
                loginAzureAd()
              }
            }
          >
            Sign In with Azure AD
          </button>
        )}
        { authType !== "AzureAD" && (
          <TriyoForm>
            <Form
              noValidate
              validated={validated}
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              <Form.Group>
                <Form.Label>Email address or user name</Form.Label>
                <Form.Control
                  className="email-input input"
                  type="text"
                  value={email}
                  onChange={onEmailChange}
                  required
                  placeholder="username@triyo.io"
                />
                <Form.Control.Feedback type="invalid">
                  Email address is required
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>Password</Form.Label>
                <div className="forget-password" onClick={forgotPassword}>
                  Forgot Password?
                </div>
                <PasswordControl
                  className="password-input input"
                  onChange={setPassword}
                  value={password}
                  isInvalid={validated && !password}
                  errorMessage="Password is required"
                />
              </Form.Group>
              <Form.Group className="remember-me">
                <Form.Check
                  type="checkbox"
                  label="Remember Me"
                  checked={rememberMe}
                  onChange={changeRememberMe}
                  inline="true"
                />
              </Form.Group>
              <Button
                className="next-button"
                type="submit"
                onClick={onSubmit}
                disabled={loading}
              >
                { loading && (
                  <Spinner
                    className="nextButtonSpinner"
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                  />
                )}
                Login
              </Button>
              { isSelfServe && (
                <div className="footer">
                  <h3>Don’t have access to TRIYO?</h3>
                  <a target="_blank" href={signupURL}>
                    Click here to sign up
                  </a>
                </div>
              )}
            </Form>
          </TriyoForm>
        )}
      </Container>
    </LoginModal>
  );
};

Login.propTypes = {
  onLogin: PropTypes.func
};

export default Login;
